import { Table } from 'antd';
import type { FilterDropdownProps, ColumnFilterItem } from 'antd/lib/table/interface';
import { isEmpty, pick, sortBy } from 'lodash/fp';
import numeral from 'numeral';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type LocalVariantsListDataFragment } from '../../../api/fragments/LocalVariantsListData';
import { useListLocalModelsQuery } from '../../../api/queries/listLocalModels';
import { useListLocalVariantsQuery } from '../../../api/queries/listLocalVariants';
import { CompanyFilterListCollection } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import FilterBox from '../../../components/FilterBox';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import useSearchInDropDown from '../../../components/useSearchInDropDown';
import renderBooleanIcon from '../../../utilities/renderBooleanIcon';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useGetCompanyFilter from '../../../utilities/useGetCompanyFilter';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useListReducer from './useListReducer';
import useSortOrder, { getSortField } from './useSortOrder';

const renderPrice = (price: number, currency: string) => `${currency} ${numeral(price).format('1,000')}`;
const filterListMapper = list =>
    list?.map(model => ({
        text: model.name.defaultValue,
        value: model.id,
    })) || [];

const VariantList = () => {
    const { t } = useTranslation('variantList');
    const company = useCompany(true);

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Variant);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    const navigate = useNavigate();

    // fetch data
    const { page, pageSize, sort, filter } = state;

    // fetch model data for filter
    const { data: allModelAndSubmodelData } = useListLocalModelsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: { companyId: company?.id },
        },
    });

    const companyFilterList = useGetCompanyFilter({ collection: CompanyFilterListCollection.LocalVariants });

    const modelFilter = sortBy(
        'text',
        filterListMapper(allModelAndSubmodelData?.list?.items.filter(vehicle => isEmpty(vehicle.parentModel)))
    );

    const submodelFilter = sortBy(
        'text',
        filterListMapper(allModelAndSubmodelData?.list?.items.filter(vehicle => !isEmpty(vehicle.parentModel)))
    );

    const { data, loading, error } = useListLocalVariantsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { companyId: company ? company.id : undefined, ...filter },
        },
    });

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filterBy: {
                            companyIds: !filters.companyId ? null : filters.companyId[0],
                            name: !filters.name ? null : { defaultValue: filters.name[0], overrides: [] },
                            identifier: !filters.identifier ? null : filters.identifier[0],
                            modelIds: !filters.modelIds ? null : filters.modelIds,
                            submodelIds: !filters.submodelIds ? null : filters.submodelIds,
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    // prepare items as a data source
    const dataSource = useMemo(() => (data?.list?.items || []).map(item => ({ ...item, key: item.id })), [data]);
    const total = data?.list?.count || 0;

    const searchBox = useSearchInDropDown();

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const { companySort, nameSort, identifierSort, subModelSort, modelSort, orderSort, priceSort, activeSort } =
        useSortOrder(sort);

    if (!loading) {
        if (error) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={(row: LocalVariantsListDataFragment) => ({
                onClick: () => {
                    setCache(state);
                    navigate(`/admin/vehicles/variants/${row.id}`);
                },
                style: { cursor: 'pointer' },
            })}
            rowKey="id"
            state={state}
            tableName={t('variantList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['module', 'company', 'displayName']}
                    filterDropdown={props => showFilterDropDown(props, companyFilterList)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter.companyIds}
                    sortOrder={companySort}
                    title={t('variantList:columns.company')}
                    sorter
                />
            )}
            <Table.Column
                key="identifier"
                dataIndex="identifier"
                filterDropdown={searchBox.render('identifier')}
                filterIcon={renderSearchIcon}
                filteredValue={filter?.identifier ? [filter.identifier] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                sortOrder={identifierSort}
                title={t('variantList:columns.identifier')}
                sorter
            />
            <Table.Column
                key="name"
                dataIndex="name"
                filterDropdown={searchBox.render('name')}
                filterIcon={renderSearchIcon}
                filteredValue={filter?.name?.defaultValue ? [filter.name.defaultValue] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                render={name => name.defaultValue}
                sortOrder={nameSort}
                title={t('variantList:columns.name')}
                sorter
            />
            <Table.Column
                key="modelIds"
                dataIndex="model"
                filterDropdown={props => showFilterDropDown(props, modelFilter)}
                filteredValue={filter.modelIds}
                render={model => (model.parentModel ? model.parentModel.name.defaultValue : model.name.defaultValue)}
                sortOrder={modelSort}
                title={t('variantList:columns.model')}
                sorter
            />
            <Table.Column
                key="submodelIds"
                dataIndex={['submodel', 'name', 'defaultValue']}
                filterDropdown={props => showFilterDropDown(props, submodelFilter)}
                filteredValue={filter.submodelIds}
                sortOrder={subModelSort}
                title={t('variantList:columns.subModel')}
                sorter
            />
            <Table.Column
                key="vehiclePrice"
                dataIndex="vehiclePrice"
                render={(vehiclePrice, record: LocalVariantsListDataFragment) =>
                    renderPrice(vehiclePrice, record.module.company.currency)
                }
                sortOrder={priceSort}
                title={t('variantList:columns.vehiclePrice')}
                sorter
            />
            <Table.Column
                key="order"
                dataIndex="order"
                sortOrder={orderSort}
                title={t('variantList:columns.order')}
                sorter
            />
            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                render={value => renderBooleanIcon(value, true)}
                sortOrder={activeSort}
                title={t('variantList:columns.isActive')}
                width={120}
                sorter
            />
        </PaginatedTableWithContext>
    );
};

export default VariantList;
